import actionCreatorFactory from 'typescript-fsa';
import { IThunk } from 'ui/reducers/store';
import * as personalInformationAPI from 'ui/interfaces/api/personalInformation';
import { IAxiosResponseError } from 'ui/utils/axios';
import { get } from 'lodash';
import * as patientPersonalTabAPI from 'ui/interfaces/api/personalInformation/personal';
import AlertService from 'ui/utils/alertService';
import * as metaDataAPI from 'ui/interfaces/api/personalInformation/meta';
import { mixpanelService } from 'ui/utils/mixpanel';
import { IRelatedMember } from 'ui/interfaces/entities/personalInformation';

const actionCreator = actionCreatorFactory('PATIENTSIDEBAR/PI');

const actions = {
  getPersonalInformation: actionCreator.async<
    void,
    personalInformationAPI.GET.IResponse,
    personalInformationAPI.GET.IResponseError
  >('getPersonalInformation'),
  putPatientPersonalTab: actionCreator.async<
    void,
    patientPersonalTabAPI.PUT.IResponse,
    patientPersonalTabAPI.PUT.IResponseError
  >('putPatientPersonalTab'),
  setCurrentMode: actionCreator<{
    action: 'view' | 'create' | 'edit' | 'delete';
    name: string;
    displayName: string;
    // Track member data for care custodian edit
    memberData?: IRelatedMember;
  }>('setCurrentMode'),
  getMetaData: actionCreator.async<void, metaDataAPI.GET.IResponse, metaDataAPI.GET.IResponseError>('getMetaData'),
  putPersonalInformation: actionCreator.async<
    void,
    personalInformationAPI.PUT.IResponse,
    personalInformationAPI.PUT.IResponseError
  >('putPatientContactTab'),
};

const thunks = {
  getPersonalInformationAsync: (empi: string): IThunk => dispatch => {
    dispatch(actions.getPersonalInformation.started());
    personalInformationAPI.GET.service(empi)
      .then(({ data }) => {
        dispatch(
          actions.getPersonalInformation.done({
            result: data,
            params: undefined,
          }),
        );
      })
      .catch((error: IAxiosResponseError) => {
        dispatch(
          actions.getPersonalInformation.failed({
            error: get(error, 'response.data', 'Something Went Wrong!'),
            params: undefined,
          }),
        );
      });
  },
  putPersonalInformationAsync: (empi: EMPI, payload: patientPersonalTabAPI.PUT.IRequest): IThunk => (
    dispatch,
    getState,
  ) => {
    const {
      sidebar: {
        personalInformation: { currentMode },
      },
    } = getState();
    dispatch(actions.putPersonalInformation.started());
    personalInformationAPI.PUT.service(empi, payload)
      .then(response => {
        let message = '';
        if (currentMode.name === 'carecustodian') {
          switch (currentMode.action) {
            case 'create':
              message = `Care custodian added`;
              mixpanelService.track('Care custodian added', {
                category: 'D P360  - Personal Information',
              });
              break;
            case 'edit':
              message = `Updated Care custodian successfully`;
              break;
            case 'delete':
              message = `Care custodian deleted`;
              break;
            default:
              break;
          }
        } else {
          message = `Updated ${currentMode.displayName} successfully.`;
        }
        AlertService.showAlert({
          title: '',
          message,
          appearance: 'success',
          autoClearAfter: 3000,
        });
        dispatch(
          actions.putPersonalInformation.done({
            result: response.data,
            params: undefined,
          }),
        );
        dispatch(actions.setCurrentMode({ action: 'view', name: '', displayName: '' }));
        dispatch(thunks.getPersonalInformationAsync(empi));
        const updateBannerFor = ['phoneNumber', 'email', 'preferredMethodofContact', 'enrolledProgram'];
        if (updateBannerFor.includes(currentMode.name)) {
          const eventVent = get(window, 'skeletonPwa.vent', undefined);
          if (eventVent) {
            eventVent.emit('datashopPatient:personalInfoUpdated');
          }
        }
      })
      .catch(error => {
        let message = 'Failed to save changes. Retry.';
        if (currentMode.name === 'carecustodian') {
          message = get(error, 'response.data.field_errors[0].related_members', 'Failed to save changes. Retry.');
        }
        AlertService.showAlert({
          title: '',
          message,
          appearance: 'alert',
          autoClearAfter: 3000,
        });
        dispatch(
          actions.putPersonalInformation.failed({
            error,
            params: undefined,
          }),
        );
      });
  },
  getMetaDataAsync: (): IThunk => dispatch => {
    dispatch(actions.getMetaData.started());
    metaDataAPI.GET.service()
      .then(response => {
        dispatch(
          actions.getMetaData.done({
            result: response.data,
            params: undefined,
          }),
        );
      })
      .catch(error => {
        dispatch(
          actions.getMetaData.failed({
            error: get(error, 'response.data', 'Something Went Wrong!'),
            params: undefined,
          }),
        );
      });
  },
};

export default { ...actions, ...thunks };
